import { datadogLogs } from '@datadog/browser-logs';
import { useCallback, useContext } from 'react';

import { Constants } from '../../common/Constants';
import { ConstantsLagoon } from '../../common/ConstantsLagoon';
import { setGeoLocationToStorage } from '../../common/PSXHeader/SearchBar/utils';
import { isCounty } from '../../common/SnackCardContainer/utils';
import { CountySearchContext } from '../../context/CountySearchContext';
import { Location } from '../../models/Location';
import { getStackTrace } from '../../utils/error.utils';
import { useFeatureFlag } from '../useFeatureFlag';
import { useLocation } from '../useLocation';
import { useUserLocation } from '../useUserLocation';

export const useDeviceLocation = (
  setDeviceLocation: (location: string) => void,
  setIsLoading?: (loading: boolean) => void,
  setCurrentLocationStatus?: (status: string) => void,
  enableMemberDefaultLocation?: boolean
) => {
  const { setIsCountySearch } = useContext(CountySearchContext);

  const [disableGeolocationHighAccuracy] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.DISABLE_GEOLOCATION_HIGH_ACCURACY,
  ]);

  // Handles runtime geolocation errors (e.g. permission denied, timeout).
  // Respects enableMemberDefaultLocation: status is only set when the member
  // default location flow is active, preserving the original fallback behaviour.
  const handleLocationError = (error?: unknown) => {
    if (error && setCurrentLocationStatus && enableMemberDefaultLocation) {
      setCurrentLocationStatus('LOCATION_NOT_FOUND');
    }
    if (setIsLoading) {
      setIsLoading(false);
    }
  };

  // Handles the case where the Geolocation API is entirely unavailable
  // (e.g. blocked by the LAWW portal, non-HTTPS context, or unsupported browser).
  // Always sets LOCATION_NOT_FOUND regardless of enableMemberDefaultLocation so
  // the widget unconditionally exits the LOADING state in all portal contexts.
  const handleGeolocationUnavailable = () => {
    if (setCurrentLocationStatus) {
      setCurrentLocationStatus('LOCATION_NOT_FOUND');
    }
    if (setIsLoading) {
      setIsLoading(false);
    }
  };

  const [, getLocation] = useLocation({
    onCompleted: (result) => {
      const targetLocation: Location =
        result?.data?.location?.features?.[0] || {};
      const {
        id,
        center = [],
        place_name: placeName,
        stateCode,
        zipCode,
      } = targetLocation;
      const [long, lat] = center;
      // set geo coords
      if (isCounty(id)) setIsCountySearch(true);
      else setIsCountySearch(false);
      if (setIsLoading) {
        setIsLoading(false);
      }
      if (Object.keys(targetLocation).length === 0) {
        return;
      }
      setGeoLocationToStorage({
        id,
        name: placeName,
        longitude: long,
        latitude: lat,
        stateCode,
        zipCode,
      });
    },
  });

  const [, getUserLocation] = useUserLocation({
    onCompleted: (result: {
      data: { userLocation: { features: { place_name: string }[] } };
    }) => {
      const { ADDRESS_CITY_STATE_ZIPCODE } = Constants.DEVICE_LOCATION;
      const devicePlaceName = result?.data?.userLocation?.features?.[
        ADDRESS_CITY_STATE_ZIPCODE
      ]?.place_name?.slice(0, -15);

      if (!devicePlaceName?.trim()) {
        datadogLogs.logger.warn(
          '[useDeviceLocation] Blank device place name, location lookup skipped',
          {
            rawPlaceName:
              result?.data?.userLocation?.features?.[ADDRESS_CITY_STATE_ZIPCODE]
                ?.place_name ?? null,
            pathname: window?.location?.pathname,
            stackTrace: getStackTrace(new Error()),
          }
        );
        return;
      }

      getLocation({
        variables: {
          address: encodeURIComponent(devicePlaceName),
          countySearchEnabled: true,
        },
      });
      setDeviceLocation(devicePlaceName);
    },
    onError: (error) => {
      handleLocationError(error);
    },
  });

  const handleFindLocation = useCallback(
    async (longitude: string, latitude: string) => {
      await getUserLocation({
        variables: {
          longitude,
          latitude,
        },
      });
    },
    [getUserLocation]
  );

  const options = {
    enableHighAccuracy: !disableGeolocationHighAccuracy,
  };

  const getDeviceLocation = () => {
    const successCallback = (position: {
      coords: { latitude: number; longitude: number };
    }) => {
      const { latitude, longitude } = position.coords;
      handleFindLocation(String(longitude), String(latitude));
    };

    // Guard: Geolocation API blocked or unavailable (e.g. LAWW portal, non-HTTPS, old browser).
    // Use dedicated handler that always exits LOADING — no enableMemberDefaultLocation gate.
    if (!navigator.geolocation) {
      handleGeolocationUnavailable();
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        successCallback(position);
      },
      (error) => {
        // Runtime error (permission denied, timeout, etc.) — respects enableMemberDefaultLocation
        handleLocationError(error);
      },
      options
    );
  };

  return { getDeviceLocation };
};
